import React, { useState, useEffect, useRef } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { Box, Flex, Text, Group, TextInput, Button, Pagination, Modal, ScrollArea, Select } from '@mantine/core'
import classes from '../../../../assets/module-css/AdminLayout.module.css'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { getAllCityList, deleteCity, getAllCountryList, getAllProvinceList } from '../../../../services/location'
import { useDebouncedCallback } from '@mantine/hooks'
import LoadingData from '../../../../component/ui/LoadingData'
import NoData from '../../../../component/ui/NoData'
import TableCity from '../../../../component/pages/City/TableCity'
import FormCity from '../../../../component/pages/City/FormCity'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX, IconPlus } from '@tabler/icons-react'
import { modals } from '@mantine/modals'

const defaultParameter = {
    'page[num]': 1,
    'page[limit]': 10,
    sort: '-id',
    include: 'countries,provinces'
}

const labelTable = [
    { label: 'ID', width: 80 },
    { label: 'City Name', width: 'auto' },
    { label: 'Province', width: 250 },
    { label: 'Country', width: 250 },
    { label: 'Action', width: 100 },
]

const CityList = () => {
    const dispatch = useDispatch()
    const [params, setParams] = useState(defaultParameter)
    const [cityList, setCityList] = useState([])
    const [totalData, setTotalData] = useState(0)
    const [loading, setLoading] = useState(true)
    const [openModalForm, setOpenModalForm] = useState(false)
    const [detailData, setDetailData] = useState(null)
    const [allCountries, setAllCountries] = useState([])
    const [provinceFilterList, setProvinceFilterList] = useState([])
    const [loadingProvinces, setLoadingProvinces] = useState(false)

    // We might not be able to fetch ALL provinces efficiently, so we rely on 'included' or on-demand

    const countriesRef = useRef([])

    const mappingRoute = [
        { label: '/', route: null },
        { label: 'Settings', route: null },
        { label: 'City List', route: null },
    ]

    const handleGetAllCityList = useDebouncedCallback(async () => {
        setLoading(true)
        try {
            const response = await getAllCityList(params)

            if (response.success || response.data) {
                const startData = response.data || []
                const included = response.included || []
                const countData = response.meta?.pagination?.total || 0

                const remapCity = startData.map((val) => {
                    let countryId = null
                    let provinceId = null

                    // 1. IDs from relationships or attributes
                    if (val.relationships?.country?.id) {
                        countryId = val.relationships.country.id
                    } else if (val.attributes?.country_id) {
                        countryId = val.attributes.country_id
                    }

                    if (val.relationships?.provinces?.id) {
                        provinceId = val.relationships.provinces.id
                    } else if (val.attributes?.province_id) {
                        provinceId = val.attributes.province_id
                    }


                    return {
                        id: val.id,
                        name: val.attributes.name,
                        country_id: countryId,
                        province_id: provinceId,
                        country_name: included.countries[countryId]?.attributes?.name || "---",
                        province_name: included.provinces[provinceId]?.attributes?.name || "---",
                    }
                })
                setCityList(remapCity)
                setTotalData(countData)
            } else {
                setCityList([])
                setTotalData(0)
            }
        } catch (error) {
            console.log(error)
            setCityList([])
        } finally {
            setLoading(false)
        }
    }, 500)

    useEffect(() => {
        dispatch(updateRoute({ 'data': mappingRoute }))

        const fetchCountries = async () => {
            try {
                const response = await getAllCountryList({ 'page[limit]': 300 })
                if (response && response.data) {
                    const mapped = response.data.map(c => ({
                        value: String(c.attributes.id),
                        label: c.attributes.name
                    }))
                    setAllCountries(mapped)
                    countriesRef.current = response.data
                }
            } catch (error) {
                console.error("Failed to fetch countries for mapping", error)
            }
        }
        fetchCountries()
        // eslint-disable-next-line 
    }, [dispatch])

    const fetchProvincesForFilter = async (countryId) => {
        if (!countryId) {
            setProvinceFilterList([])
            return
        }
        setLoadingProvinces(true)
        try {
            const response = await getAllProvinceList({
                'filter[country_id]': countryId,
                'page[limit]': 300
            })
            if (response && response.data) {
                const mapped = response.data.map(p => ({
                    value: String(p.id),
                    label: p.attributes.name
                }))
                setProvinceFilterList(mapped)
            } else {
                setProvinceFilterList([])
            }
        } catch (error) {
            console.error("Failed to fetch provinces", error)
        } finally {
            setLoadingProvinces(false)
        }
    }

    useEffect(() => {
        handleGetAllCityList()
        // eslint-disable-next-line
    }, [params])

    const handleDelete = (val) => {
        modals.openConfirmModal({
            title: 'Delete City',
            children: (
                <Text size="sm">
                    Are you sure you want to delete city <strong>{val.name}</strong>? This action cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onCancel: () => console.log('Cancel'),
            onConfirm: async () => {
                try {
                    const response = await deleteCity(val.id)
                    if (response.success) {
                        notifications.show({
                            title: 'Success',
                            message: 'City deleted successfully',
                            color: 'teal',
                            icon: <IconCheck />,
                        })
                        handleGetAllCityList()
                    } else {
                        notifications.show({
                            title: 'Failed',
                            message: response.message || 'Failed to delete city',
                            color: 'red',
                            icon: <IconX />,
                        })
                    }
                } catch (error) {
                    notifications.show({
                        title: 'Error',
                        message: error.message || 'Something went wrong',
                        color: 'red',
                        icon: <IconX />,
                    })
                }
            },
        })
    }

    const actions = {
        edit: (val) => {
            setDetailData(val)
            setOpenModalForm(true)
        },
        delete: (val) => {
            handleDelete(val)
        }
    }

    const handleAction = (val, type) => {
        if (actions[type]) {
            actions[type](val)
        }
    }

    const handleChangeFilter = (name, val) => {
        setParams((oldVal) => ({ ...oldVal, [name]: val }))
    }

    const handleChangePage = (val) => {
        setParams((oldVal) => ({ ...oldVal, 'page[num]': val }))
    }

    const onCloseModalForm = () => {
        setOpenModalForm(false)
        setDetailData(null)
    }

    const handleAdd = () => {
        setDetailData(null)
        setOpenModalForm(true)
    }

    return (
        <AuthLayout>
            <Box my={20}>
                <Box>
                    <Flex justify='space-between' align="center" mb={30}>
                        <Text className={classes.titleLayout}>City List</Text>
                        <Group gap='sm' align='center'>
                            <TextInput
                                value={params['filter[name][like]'] || ''}
                                placeholder='Search by name'
                                onChange={(val) => {
                                    handleChangeFilter('filter[name][like]', val.target.value)
                                    handleChangePage(1)
                                }}
                                size='xs'
                            />
                            <Select
                                placeholder="Filter Country"
                                data={allCountries}
                                value={params['filter[country_id]'] || null}
                                onChange={(val) => {
                                    handleChangeFilter('filter[country_id]', val)
                                    // Reset province when country changes
                                    handleChangeFilter('filter[province_id]', null)
                                    fetchProvincesForFilter(val)
                                    handleChangePage(1)
                                }}
                                searchable
                                clearable
                                size='xs'
                                w={200}
                            />
                            <Select
                                placeholder={loadingProvinces ? "Loading..." : "Filter Province"}
                                data={provinceFilterList}
                                value={params['filter[province_id]'] || null}
                                onChange={(val) => {
                                    handleChangeFilter('filter[province_id]', val)
                                    handleChangePage(1)
                                }}
                                searchable
                                clearable
                                size='xs'
                                w={200}
                                disabled={!params['filter[country_id]']}
                            />
                            <Button size='xs' leftSection={<IconPlus size={14} />} onClick={handleAdd}>Add City</Button>
                        </Group>
                    </Flex>
                </Box>

                <Box my={30}>
                    <Group mb={10} gap='xs'>
                        <Text fz={12}>Total Data Found: </Text>
                        <Text fz={12} fw='bold'>{totalData} Cities </Text>
                    </Group>
                    {loading ? <LoadingData /> : cityList.length > 0 ? <TableCity label={labelTable} data={cityList} actionMethod={handleAction} /> : <NoData />}
                </Box>
                <Flex justify='end'>
                    <Pagination size='sm' onChange={handleChangePage} value={params['page[num]']} total={totalData > 0 ? Math.ceil(totalData / params['page[limit]']) : 0} />
                </Flex>
            </Box>

            <Modal opened={openModalForm} onClose={onCloseModalForm} centered closeOnClickOutside={false} size='md' title={<Text fw='Bold'>{detailData ? 'Edit City' : 'Add City'}</Text>} scrollAreaComponent={ScrollArea.Autosize} withCloseButton={false}>
                <FormCity dataDetail={detailData} onCloseModal={onCloseModalForm} reloadList={handleGetAllCityList} />
            </Modal>
        </AuthLayout>
    )
}

export default CityList
